import { CheckRounded, KeyboardArrowDown } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Radio,
  Switch,
} from "@mui/material";
import { useCallback, useMemo, useRef } from "react";
import StyledMenu from "./StyledMenu";

/**
 * Reusable button that opens a styled dropdown menu of selectable options.
 *
 * Options support two modes:
 *   - Standard selectable items (click to select/deselect)
 *   - Toggle items with a Switch (type: "toggle")
 *
 * @param {object}   props
 * @param {Array}    props.options          – Array of option objects:
 *   Standard:  { label, value, icon?, dividerAfter? }
 *   Toggle:    { label, value, icon?, type: "toggle", checked: boolean, onChange: (checked) => void }
 * @param {*}        props.value            – Currently selected value (for standard options)
 * @param {function} props.onChange         – Called with the selected option's value (for standard options)
 * @param {string}   [props.buttonText]    – Default button text when nothing is selected
 * @param {import("@mui/material").SvgIconComponent} [props.startIcon] – Icon before the button text
 * @param {boolean}  [props.allowDeselect] – If true, clicking the active option deselects it
 * @param {boolean}  [props.showBadge]     – Show a dot badge when a value is active
 * @param {string}   [props.menuTitle]     – Optional title shown at the top of the menu
 * @param {number}   [props.menuMinWidth]  – Min width of the menu paper (default: 240)
 * @param {string}   [props.size]          – Button size (default: "medium")
 * @param {string}   [props.variant]       – Button variant (default: "outlined")
 * @param {React.ReactNode} [props.children]       – Custom content rendered below the option list
 * @param {React.ReactNode} [props.headerContent]  – Custom content rendered above the option list
 * @param {object}   [props.sx]            – Additional sx for the root Box
 * @param {object}   [props.buttonSx]      – Additional sx for the Button
 */
const ButtonMenu = ({
  options = [],
  value,
  onChange,
  buttonText = "Menu",
  startIcon,
  allowDeselect = false,
  showBadge = false,
  menuTitle,
  menuMinWidth = 240,
  size = "medium",
  variant = "outlined",
  children,
  headerContent,
  sx = {},
  buttonSx = {},
}) => {
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = useMemo(
    () =>
      options.find((opt) => opt.type !== "toggle" && opt.value === value) ??
      null,
    [options, value],
  );

  // Check if any toggle option is active (for badge)
  const hasActiveToggle = useMemo(
    () => options.some((opt) => opt.type === "toggle" && opt.checked),
    [options],
  );

  const isActive =
    selectedOption !== null || hasActiveToggle || (showBadge && value);

  const handleSelect = useCallback(
    (optionValue) => {
      if (allowDeselect && value === optionValue) {
        onChange?.(null);
      } else {
        onChange?.(optionValue);
      }
      menuRef.current?.close();
    },
    [value, allowDeselect, onChange],
  );

  const displayText = useMemo(() => {
    if (selectedOption) {
      return selectedOption?.label;
      //   return (
      //     <>
      //       <b>{selectedOption.label}</b>
      //       {/* {buttonText}: <b style={{ marginLeft: 4 }}>{selectedOption.label}</b> */}
      //     </>
      //   );
    }
    return buttonText;
  }, [selectedOption, buttonText]);

  const buttonContent = (
    <Button
      ref={anchorRef}
      startIcon={startIcon}
      endIcon={<KeyboardArrowDown />}
      size={size}
      variant={variant}
      color={isActive ? "primary" : "gray"}
      onClick={() => menuRef.current?.toggle()}
      sx={{
        border: "1px solid #e0e0e0",
        textTransform: "none",
        borderRadius: "8px",
        fontWeight: isActive ? 600 : 400,
        px: 1.2,
        ...buttonSx,
      }}
    >
      {displayText}
    </Button>
  );

  return (
    <Box sx={{ display: "inline-flex", ...sx }}>
      {showBadge ? (
        <Badge color="primary" variant="dot" invisible={!isActive}>
          {buttonContent}
        </Badge>
      ) : (
        buttonContent
      )}

      <StyledMenu
        ref={menuRef}
        anchorRef={anchorRef}
        title={menuTitle}
        minWidth={menuMinWidth}
      >
        {/* Header content */}
        {headerContent}

        {/* Options */}
        {options.length > 0 && (
          <MenuList
            sx={{
              py: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            {options.map((option, index) => {
              const isToggle = option.type === "toggle";
              const isSelected = !isToggle && value === option.value;

              return (
                <Box key={option.value ?? `__opt_${index}__`}>
                  <MenuItem
                    onClick={() => {
                      if (isToggle) {
                        option.onChange?.(!option.checked);
                      } else {
                        handleSelect(option.value);
                      }
                    }}
                    selected={isSelected}
                    sx={{
                      py: 1,
                      px: 2,
                      mx: 0.75,
                      borderRadius: "8px",
                      fontSize: 14,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    {option.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color:
                            isSelected || (isToggle && option.checked)
                              ? "primary.main"
                              : "text.secondary",
                        }}
                      >
                        {option.icon}
                      </ListItemIcon>
                    )}
                    {!isToggle && (
                      <ListItemIcon sx={{ minWidth: "unset" }}>
                        <Radio
                          checked={isSelected}
                          size="small"
                          sx={{
                            p: 0,
                            color: "#d1d5db",
                            "&.Mui-checked": { color: "#2563eb" },
                          }}
                          disableRipple
                        />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{
                        fontSize: "13px",
                        fontWeight:
                          isSelected || (isToggle && option.checked)
                            ? 600
                            : 400,
                        color:
                          isSelected || (isToggle && option.checked)
                            ? "#1d4ed8"
                            : "#374151",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    />

                    {/* Toggle switch for toggle-type options */}
                    {isToggle && (
                      <Switch
                        size="small"
                        checked={option.checked}
                        onChange={(e) => {
                          e.stopPropagation();
                          option.onChange?.(e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ ml: 2 }}
                      />
                    )}

                    {/* Checkmark for standard selected options */}
                    {isSelected && !isToggle && (
                      <CheckRounded
                        sx={{ fontSize: 18, color: "primary.main" }}
                      />
                      //   <ListItemIcon sx={{ minWidth: "unset", ml: 2 }}>
                      //     <CheckRounded
                      //       sx={{ fontSize: 18, color: "primary.main" }}
                      //     />
                      //   </ListItemIcon>
                    )}
                  </MenuItem>
                  {option.dividerAfter && <Divider sx={{ my: 0.5 }} />}
                </Box>
              );
            })}
          </MenuList>
        )}

        {/* Custom children */}
        {children}
      </StyledMenu>
    </Box>
  );
};

export default ButtonMenu;
